import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import {
    Badge,
    Card,
    CardBody,
    Container,
    Row,
    Col
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import MetaTag from "components/Common/Meta-tag"

import { useSelector, useDispatch } from "react-redux"
import { getInvestmentLeadDetails } from "store/actions"
import moment from "moment"

import FollowUpDataTables from "pages/followUp/DataTables"
import FollowUpModal from "pages/followUp/Crud/Modal"

const InvestmentLeadDetails = () => {
    const dispatch = useDispatch()
    const params = useParams()

    const [followUpModal, setFollowUpModal] = useState({ status: false, data: {} })

    const { details, loading } = useSelector(state => ({
        details: state.InvestmentLead.clientDetails?.investmentLead || state.InvestmentLead.clientDetails || state.InvestmentLead.investment_leadDetails || {},
        loading: state.InvestmentLead.loading,
    }))

    useEffect(() => {
        if (params?.id) {
            dispatch(getInvestmentLeadDetails(params.id))
        }
    }, [dispatch, params?.id])

    // Helpers for styling
    const handleStatusColor = status => {
        switch (status) {
            case "new": return "primary"
            case "process": return "info"
            case "win": return "success"
            case "lost": return "warning"
            default: return "secondary"
        }
    }

    const handleLeadConditionColor = (condition) => {
        switch (condition) {
            case "superHot": return "danger"
            case "hot": return "warning"
            case "warm": return "info"
            case "normal": return "primary"
            default: return "secondary"
        }
    }

    // To match user format
    const data = details || {}

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTag title="Investment Lead Details" />

                <Container fluid>
                    <Breadcrumbs title="Investment Lead" breadcrumbItem="Investment Lead Details" />

                    {followUpModal?.status && (
                        <FollowUpModal
                            onCloseClick={() => setFollowUpModal({ status: false, data: {} })}
                            show={followUpModal?.status}
                            updateData={followUpModal?.data}
                            type="call"
                            sort="all"
                            task="all"
                        />
                    )}

                    <div style={{ display: "flex", gap: "20px" }}>
                        {/* Left Section - Client Details & Follow Ups */}
                        <div
                            className="hide-scrollbar"
                            style={{
                                width: "60%",
                                height: "calc(100vh - 150px)",
                                overflowY: "auto",
                                overflowX: "hidden",
                                paddingRight: "10px"
                            }}
                        >
                            <Card className="mb-3">
                                <CardBody className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div style={{ flex: 1 }}>
                                            <div className="d-flex align-items-center mb-2">
                                                <div
                                                    className="d-flex align-items-center justify-content-center me-3"
                                                    style={{
                                                        width: "48px",
                                                        height: "48px",
                                                        backgroundColor: "#deebff",
                                                        borderRadius: "50%"
                                                    }}
                                                >
                                                    <i className="bx bx-user text-primary" style={{ fontSize: "24px" }} />
                                                </div>
                                                <div className="d-flex align-items-center flex-wrap" style={{ gap: "3px" }}>
                                                    <h4 className="m-0 fw-semibold">{data?.name || "No Name"}</h4>

                                                    {data?.status && (
                                                        <Badge
                                                            className={"ms-1 font-size-11 badge-soft-" + handleStatusColor(data?.status)}
                                                            pill
                                                            style={{ textTransform: "capitalize" }}
                                                        >
                                                            {data?.status}
                                                        </Badge>
                                                    )}

                                                    {data?.leadCondition && (
                                                        <Badge className={"ms-1 font-size-11 badge-soft-" + handleLeadConditionColor(data?.leadCondition)} pill style={{ textTransform: "capitalize" }}>
                                                            {data?.leadCondition}
                                                        </Badge>
                                                    )}

                                                    {data?.type && (
                                                        <Badge className="ms-1 font-size-11 badge-soft-dark" pill style={{ textTransform: "capitalize" }}>
                                                            {data?.type}
                                                        </Badge>
                                                    )}

                                                    {data?.medium && (
                                                        <Badge className="ms-1 font-size-11 badge-soft-info" pill style={{ textTransform: "capitalize" }}>
                                                            {data?.medium}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>

                                            <Row className="mt-4 gx-3 gy-2" style={{ fontSize: "14px" }}>
                                                <Col sm="6" md="4" className="d-flex align-items-center">
                                                    <i className="bx bxs-phone text-primary me-2 fs-5" />
                                                    Phone: <span className="ms-2">{data?.phone || "---"}</span>
                                                </Col>

                                                <Col sm="6" md="4" className="d-flex align-items-center">
                                                    <i className="bx bx-briefcase text-success me-2 fs-5" />
                                                    Occupation: <span className="ms-2">{data?.occupation || "---"}</span>
                                                </Col>

                                                <Col sm="6" md="4" className="d-flex align-items-center">
                                                    <i className="bx bxs-map text-danger me-2 fs-5" />
                                                    Place: <span className="ms-2">{data?.place || "---"}</span>
                                                </Col>
                                            </Row>

                                            {data?.note && (
                                                <div className="mt-4 p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                                                    <h6 className="text-muted mb-2">Note:</h6>
                                                    <p className="m-0 text-dark" style={{ whiteSpace: "pre-wrap" }}>{data?.note}</p>
                                                </div>
                                            )}

                                            <div className="mt-4 d-flex justify-content-between align-items-center text-muted" style={{ fontSize: "12px" }}>
                                                <div>
                                                    <strong>Created:</strong> {data?.createdAt ? moment(data?.createdAt).format("DD MMM YYYY, h:mm A") : "---"}
                                                </div>
                                                <div>
                                                    <strong>Last Updated:</strong> {data?.updatedAt ? moment(data?.updatedAt).format("DD MMM YYYY, h:mm A") : "---"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>

                            {/* FollowUps Data Table component */}
                            <div className="mb-2">
                                <FollowUpDataTables />
                            </div>

                        </div>
                    </div>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default InvestmentLeadDetails
